/**
 * Entities: the things fields describe.
 *
 * The only consequential choice on this screen is "one or many". It decides
 * whether answers are an object or a list, and therefore whether the portal
 * draws a form or a grid - so it is presented as a plain question with its
 * consequence written next to it, and it locks once a product depends on it.
 */
import { useState, type FormEvent } from "react";
import { ApiError, api } from "../../lib/api.ts";
import { useAsync } from "../../lib/useAsync.ts";
import { useAuth } from "../../lib/auth.tsx";
import type { EntityRow } from "../../lib/types.ts";
import { Loading, PageHead, Problem, Tiles } from "../../components/Page.tsx";

const CATEGORIES = ["Policy", "Applicant", "Risk", "Coverage", "Other"] as const;

export function Entities() {
  const { can } = useAuth();
  const entities = useAsync(() => api.get<{ entities: EntityRow[] }>("/api/entities"), []);
  const [adding, setAdding] = useState(false);

  const rows = entities.data?.entities ?? [];

  return (
    <div className="stack">
      <PageHead
        title="Entities"
        sub="What a field can be about. An entity that repeats — locations, vehicles, drivers — becomes a grid on the form."
        actions={can("configuration", "edit") && !adding && (
          <button className="btn btn-primary" type="button" onClick={() => setAdding(true)}>New entity</button>
        )}
      />

      <Tiles
        tiles={[
          { label: "Entities", value: rows.length, tone: "accent" },
          { label: "Repeating", value: rows.filter((row) => row.repeating).length, tone: "info" },
          { label: "Fields defined", value: rows.reduce((total, row) => total + row.fieldCount, 0), tone: "ok" },
          { label: "Unused", value: rows.filter((row) => row.fieldCount === 0).length, tone: "muted" },
        ]}
      />

      {adding && <EntityForm onDone={() => { setAdding(false); entities.reload(); }} onCancel={() => setAdding(false)} />}

      <Problem error={entities.error} />

      <div className="card">
        {entities.loading && !entities.data ? (
          <Loading what="entities" />
        ) : rows.length === 0 ? (
          <div className="empty">
            <h3>No entities yet</h3>
            <p className="small">Start with the applicant and the policy; add risk entities as products need them.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr><th>Entity</th><th>Key</th><th>Category</th><th>How many</th><th>Fields</th></tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.key}>
                    <td>{row.label}</td>
                    <td><span className="key">{row.key}</span></td>
                    <td><span className="chip">{row.category}</span></td>
                    <td>
                      {row.repeating
                        ? <span className="chip chip-info">Many per application</span>
                        : <span className="chip">One per application</span>}
                    </td>
                    <td>{row.fieldCount || <span className="muted">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function EntityForm({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const [label, setLabel] = useState("");
  const [key, setKey] = useState("");
  const [category, setCategory] = useState<(typeof CATEGORIES)[number]>("Risk");
  const [repeating, setRepeating] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const derived = key.trim() || label.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setProblem(null);
    setBusy(true);
    try {
      await api.post("/api/entities", { key: derived, label: label.trim(), category, repeating });
      onDone();
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "Could not create this entity.");
      setBusy(false);
    }
  }

  return (
    <form className="card" onSubmit={submit}>
      <div className="card-head"><h2>New entity</h2></div>
      <div className="card-body stack">
        {problem && <div className="notice notice-bad">{problem}</div>}
        <div className="field-row">
          <div className="field">
            <label htmlFor="entity-label">Name<span className="required-mark">*</span></label>
            <input id="entity-label" className="control" value={label} required autoFocus
              placeholder="Location" onChange={(event) => setLabel(event.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="entity-key">Key</label>
            <input id="entity-key" className="control" value={key} placeholder={derived || "location"}
              onChange={(event) => setKey(event.target.value)} />
            <span className="hint">Permanent. Left blank, it comes from the name.</span>
          </div>
          <div className="field">
            <label htmlFor="entity-category">Category</label>
            <select id="entity-category" className="control" value={category}
              onChange={(event) => setCategory(event.target.value as (typeof CATEGORIES)[number])}>
              {CATEGORIES.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
            <span className="hint">Groups the field picker.</span>
          </div>
        </div>

        <label className="checkline">
          <input type="checkbox" checked={repeating} onChange={(event) => setRepeating(event.target.checked)} />
          <span>
            An application can have many of these
            <div className="small muted">
              Locations, vehicles, drivers. The portal draws a grid, and answers are stored as a list. This cannot change
              once a product collects it.
            </div>
          </span>
        </label>
      </div>
      <div className="card-foot">
        <button type="button" className="btn" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? "Creating…" : "Create entity"}</button>
      </div>
    </form>
  );
}
